/**
 * BatchScenariosModal — "Run all scenarios" regression matrix.
 *
 * Fires POST /admin/wfe/scenarios/run-all against every recorded scenario
 * and renders a per-scenario green/red matrix with pass/fail details.
 *
 * Also shows the last N nightly batch runs (from
 * GET /admin/wfe/scenarios/batch-runs) so the admin can see when the
 * scheduler last ran + whether it passed.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Clock, Play, RefreshCw, X, XCircle } from "lucide-react";

function fmtStamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BatchScenariosModal({ open, onClose }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get("/admin/wfe/scenarios/batch-runs", { params: { limit: 10 } });
      setHistory(r.data?.runs || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setResult(null);
      loadHistory();
    }
  }, [open, loadHistory]);

  if (!open) return null;

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/wfe/scenarios/run-all", { send_email: false });
      setResult(r.data);
      const { total, passed, failed } = r.data || {};
      if (failed === 0) {
        toast.success(`✅ All ${total} scenario${total === 1 ? "" : "s"} passed`);
      } else {
        toast.error(`❌ ${failed}/${total} scenario${total === 1 ? "" : "s"} failed`);
      }
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="batch-scenarios-modal"
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600">
              <Play className="w-3 h-3" /> Regression suite
            </div>
            <h2 className="mt-1 font-display text-lg font-bold text-slate-900">
              Run all recorded scenarios
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Replays every recorded scenario against its current live workflow definition and evaluates all assertions.
              Also runs nightly at 04:15 UTC — you&apos;ll get an email if anything fails.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="batch-scenarios-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={runAll}
            disabled={running}
            data-testid="batch-scenarios-run"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {running ? "Running…" : "Run all now"}
          </button>
        </div>

        {result && (
          <div className="mb-5" data-testid="batch-scenarios-result">
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold ${
                  result.failed === 0
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-rose-100 text-rose-700 border-rose-200"
                }`}
              >
                {result.failed === 0 ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {result.passed}/{result.total} passed
              </span>
              <span className="text-[10px] text-slate-500">{fmtStamp(result.ran_at)}</span>
            </div>
            <div className="space-y-1.5">
              {(result.results || []).map((r, idx) => (
                <div
                  key={r.scenario_id || idx}
                  data-testid={`batch-scenario-row-${r.scenario_id}`}
                  className={`rounded-lg border p-2 ${
                    r.passed
                      ? "bg-emerald-50/50 border-emerald-200"
                      : "bg-rose-50/50 border-rose-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {r.passed ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-900 truncate">{r.scenario_name || r.scenario_id}</div>
                      <div className="text-[10px] text-slate-500">
                        {r.workflow_slug}
                        {r.skipped ? " · SKIPPED" : ""}
                        {r.assertion_count > 0 ? ` · ${r.assertion_count} assertion${r.assertion_count === 1 ? "" : "s"}` : " · no assertions"}
                      </div>
                    </div>
                  </div>
                  {r.error && (
                    <div className="mt-1 text-[10px] font-mono text-rose-700">error: {r.error}</div>
                  )}
                  {(r.failed_assertions || []).length > 0 && (
                    <div className="mt-1 pl-6 space-y-0.5">
                      {r.failed_assertions.map((fa, i) => (
                        <div key={i} className="text-[10px] font-mono text-rose-700">
                          {fa.type}
                          {fa.key ? `[${fa.key}]` : ""} = {JSON.stringify(fa.expected)}
                          {fa.error ? ` · ${fa.error}` : ` · got ${JSON.stringify(fa.actual)}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(result.results || []).length === 0 && (
                <div className="text-xs text-slate-500 italic">No scenarios recorded yet.</div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Recent runs</h3>
          </div>
          {loadingHistory ? (
            <div className="text-xs text-slate-500 italic">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No runs yet.</div>
          ) : (
            <div className="space-y-1" data-testid="batch-scenarios-history">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-2 text-[11px] text-slate-700">
                  {h.failed === 0 ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  ) : (
                    <XCircle className="w-3 h-3 text-rose-600" />
                  )}
                  <span className="font-mono text-[10px] text-slate-500">{fmtStamp(h.ran_at)}</span>
                  <span>
                    {h.passed}/{h.total} passed
                  </span>
                  {h.workflow_slug && <span className="text-slate-500">· {h.workflow_slug}</span>}
                  <span className="text-slate-400">· {h.triggered_by || "system"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
